import { useChatMessagesContext } from '@/Context/Chat/ChatMessagesContext';
import useChatStore, { EVENT_ADD_MODEL } from 'Components/Chat/store';
import useThemeStore from 'Theme/store';
import { useEffect, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import { cn } from 'utils/styles';
// Mobx
import { inject, observer } from 'mobx-react';

// Icons
import ModelsIcon from '../../../../../Theme/icons/modelsIcon';
import { icons as modelIcons } from '../../../../../Theme/icons/modelsIcons/index';

const ModelProviderError = inject('store')(
  observer(({ store, item, modelsIds }) => {
    const selectedModels = useChatStore((state) => state.selectedModels);
    const setShowModelsDialog = useChatStore(
      (state) => state.setShowModelsDialog
    );
    const setAddResponseModels = useChatStore(
      (state) => state.setAddResponseModels
    );
    const replaceResponse = useChatStore((state) => state.replaceResponse);
    const setReplaceResponse = useChatStore(
      (state) => state.setReplaceResponse
    );
    const rawModelsList = useChatStore((state) => state.rawModelsList);
    const setRawModelsList = useChatStore((state) => state.setRawModelsList);

    // Chat events
    const addEvent = useChatStore((state) => state.addEvent);

    const { capabilities } = useChatMessagesContext();

    const containerRef = useRef(null);

    const { theme } = useThemeStore();

    // const [modelsList, setModelsList] = useState([]);

    useEffect(() => {
      if (rawModelsList.length == 0) {
        getModels();
      }
    }, []);

    const getModels = async () => {
      try {
        const response = await store.api.get(`/model`);
        setRawModelsList(response.data.models);
      } catch (error) {
        console.error(error);
      }
    };

    const isSmall = () => {
      return containerRef.current?.offsetWidth < 300;
    };

    const getEditorsChoice = useMemo(
      (max = 7) => {
        return rawModelsList
          .filter(
            (item) => !modelsIds.some((selection) => selection == item._id)
          ) // exclude item models
          .filter((item) => item.editors_choice_level >= 3)
          .filter((model) => {
            if (capabilities.length === 0) return true;
            return model.function_calls.some((fc) =>
              capabilities.some((cap) => cap._id === fc.capability)
            );
          })
          .slice(0, max);
      },
      [rawModelsList, capabilities]
    );

    const getIconByModel = (icon, className = 'h-6 w-6') => {
      const Icon = get(modelIcons, icon);
      return Icon ? (
        <Icon className={className} />
      ) : (
        <ModelsIcon className={className} />
      );
    };

    const handleReplace = (item) => {
      setReplaceResponse(item);
      setAddResponseModels(true);
    };

    return (
      <div
        className={cn('font-figtree text-base font-normal', {
          'text-manchester-brown': theme === 'light',
          'text-soft-peach': theme === 'dark',
        })}
      >
        <div className="mb-4">
          <div>
            <span className="font-bold">
              The model provider couldn't process your request
            </span>{' '}
            (and no coins were deducted).
          </div>
          <br />
          <p>
            This could be due to content moderation, excessively large context
            for the model, high demand on the provider's end, or novel models
            (beta) potentially facing global rate limits per minute.
          </p>
          <br />

          <p>
            We recommend switching to one of the following alternative models.
            Just click any option below to replace the failed one.
          </p>
        </div>
        <div
          ref={containerRef}
          className={`grid ${
            isSmall() ? 'grid-cols-1' : 'grid-cols-2'
          } gap-x-5 gap-y-4 text-sm mb-2 mx-2`}
        >
          {getEditorsChoice
            .sort((a, b) => b.name.length - a.name.length)
            .map((model) => (
              <div
                className={cn(
                  'flex gap-3 select-none p-2 shadow-[0px_2px_7.6px_0px_rgba(0,0,0,0.1)] rounded-[10px] items-center',
                  {
                    'bg-white': theme === 'light',
                    'hover:bg-ghost-white':
                      theme === 'light' && !replaceResponse,
                    'bg-night-black': theme === 'dark',
                    'hover:bg-bg-ship-grey':
                      theme === 'light' && !replaceResponse,
                    'cursor-pointer': !replaceResponse,
                  }
                )}
                onClick={() => {
                  if (!replaceResponse) {
                    handleReplace(item);
                    addEvent(EVENT_ADD_MODEL, [model]);
                  }
                }}
              >
                <div>{getIconByModel(model.icon)}</div>
                <div className="line-clamp-2">{model.name}</div>
              </div>
            ))}

          {/* Choose another */}
          <div
            className={cn(
              'flex gap-3 select-none p-2 shadow-[0px_2px_7.6px_0px_rgba(0,0,0,0.1)] rounded-[10px] items-center',
              {
                'bg-white': theme === 'light',
                'hover:bg-ghost-white': theme === 'light' && !replaceResponse,
                'bg-night-black': theme === 'dark',
                'hover:bg-ship-grey': theme === 'dark' && !replaceResponse,
                'cursor-pointer': !replaceResponse,
              }
            )}
            onClick={() => {
              if (!replaceResponse) {
                handleReplace(item);
                setShowModelsDialog(true);
              }
            }}
          >
            <div className="bg-nue-blue rounded-lg w-6 h-6 flex items-center justify-center">
              {getIconByModel(null, 'h-5 w-5 stroke-white')}
            </div>
            <div>Choose another...</div>
          </div>
        </div>
      </div>
    );
  })
);

export default ModelProviderError;
